<template>
  <div class="row">
    <div class="col-12">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h2>{{ $t('invoices.invoice', 2).capitalize() }}</h2>
        <router-link class="btn btn-primary" :to="{name: ROUTES.DUNNING.SEPA_DIRECT_DEBITS}">
          {{ $t('sepa_direct_debits.headline') }}
        </router-link>
      </div>
    </div>
    <div class="col-12">


      <DataTable stripedRows :lazy="true"
                 :paginator="true"
                 :rows="dunningStore.serverOptions.rows"
                 :totalRecords="dunningStore.itemLength"
                 dataKey="id"
                 filterDisplay="row"
                 v-model:filters="filters"
                 :sortField="dunningStore.serverOptions.sortField"
                 :sortOrder="dunningStore.serverOptions.sortOrder"
                 :value="dunningStore.invoices"
                 :loading="dunningStore.loading"
                 paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                 :rowsPerPageOptions="[10,20,50]"
                 currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                 @filter="onFilter($event)"
                 @page="onPage($event)"
                 @sort="onSort($event)"
                 responsiveLayout="scroll">
        <TableColumn field="id" :header="this.$t('invoices.id')"></TableColumn>
        <TableColumn field="client_kdnr" :header="this.$t('invoices.client_kdnr')">
          <template #body="{data}">
            {{ data.client?.kdnr || '-' }}
          </template>
        </TableColumn>
        <TableColumn field="client_name" :header="this.$t('invoices.client_name')">
          <template #body="{data}">
            {{ data.client?.name || '-' }}
          </template>
        </TableColumn>
        <TableColumn field="number" :sortable="true" :header="this.$t('invoices.number')">
          <template #body="{data}">
            {{ data.number || `-- ${$t('draft')} ${data.id} --` }}
          </template>
          <template #filter="{filterModel,filterCallback}">
            <input type="text" v-model="filterModel.value" @input="filterCallback()" class="form-control"/>
          </template>
        </TableColumn>
        <TableColumn field="payment_method_id" :header="this.$t('invoices.payment_method')">
          <template #body="{data}">
            {{ data.payment_method?.name || '-' }}
          </template>
          <template #filter="{filterModel,filterCallback}">
            <select v-model="filterModel.value" @change="filterCallback()" class="form-select">
              <option :value="null">-</option>
              <option :value="paymentMethod.id" :key="paymentMethod.id" v-for="paymentMethod in setupStore.paymentMethods">
                {{ paymentMethod.name }}
              </option>
            </select>
          </template>
        </TableColumn>
        <TableColumn field="invoice_date" :sortable="true" :header="this.$t('invoices.date')">
          <template #body="{data}">
            {{ formatDate(data.invoice_date, 'short2') || '-' }}
          </template>
        </TableColumn>
        <TableColumn field="due_date" :sortable="true" :header="this.$t('invoices.due_date')">
          <template #body="{data}">
            <div class="text-nowrap text-center" :class="{ 'bg-warning': data.overdue }">
              {{ data.due_date ? $d(data.due_date, 'short2') : '-' }}
              <template v-if="data.days_after_due_date && data.days_after_due_date > 0">
                ({{ data.days_after_due_date }} {{ $t('invoices.days_after_due_date', data.days_after_due_date) }})
              </template>
            </div>
          </template>
        </TableColumn>
        <TableColumn field="reminder1_due_date" :sortable="true" :header="this.$t('invoices.reminder1_due_date')">
          <template #body="{data}">
            <div class="text-nowrap text-center">
              {{ formatDate(data.reminder1_due_date, 'short2') || '-' }}
            </div>
          </template>
        </TableColumn>
        <TableColumn field="reminder2_due_date" :sortable="true" :header="this.$t('invoices.reminder2_due_date')">
          <template #body="{data}">
            <div class="text-nowrap text-center">
              {{ formatDate(data.reminder2_due_date, 'short2') || '-' }}
            </div>
          </template>
        </TableColumn>
        <TableColumn field="invoice_status_id" :header="this.$t('invoices.status')">
          <template #body="slotProps">
            {{ setupStore.getInvoiceStatusById(slotProps.data.invoice_status_id)?.name }}
          </template>
          <template #filter="{filterModel,filterCallback}">
            <select v-model="filterModel.value" @change="filterCallback()" class="form-select">
              <option :value="null">-</option>
              <option :value="invoiceStatus.id" :key="invoiceStatus.id" v-for="invoiceStatus in dunningStatuses">
                {{ invoiceStatus.name }}
              </option>
            </select>
          </template>
        </TableColumn>
        <TableColumn field="net_total" :sortable="true" :header="this.$t('invoices.total')">
          <template #body="{data}">
            <div class="currency">
              {{ formatCurrency(data.net_total) }}
            </div>
          </template>
        </TableColumn>
        <TableColumn :header="this.$t('accounts.operations')">
          <template #body="slotProps">
            <div class="operations-wrapper">
              <span class="operation-item" @click="openInvoice(slotProps.data)">
              <template v-if="slotProps.data.number">
                <i class="fa-fw fa-solid fa-file-pdf"></i>
              </template>
              <template v-else>
                <i class="fa-fw fas fa-eye"/>
              </template>
            </span>
              <router-link class="operation-item" :to="{name: ROUTES.DUNNING.EDIT, params: {invoice_id: slotProps.data.id}}"
                           :title="$t('invoices.dunning_edit')"
              >
                <i class="fas fa-file-invoice-dollar"/>
              </router-link>
            </div>
          </template>
        </TableColumn>
        <ColumnGroup type="footer">
          <TableRow>
            <TableColumn :colspan="10" footerStyle="text-align:right">
              <template #footer>
                <i class="fa-regular fa-sigma"></i>
              </template>
            </TableColumn>
            <TableColumn :footer="formatCurrency(rowSums.netto)" footerStyle="text-align:right"/>
          </TableRow>
        </ColumnGroup>
      </DataTable>
    </div>
  </div>
</template>

<script>
import {useDunningStore} from "@/stores/dunning";
import {useSetupStore} from "@/stores/setup";
import {INVOICE_STATUS, ROUTES} from "@/scripts/constants";
import {FilterMatchMode} from "primevue/api";

export default {
  name: 'DunningIndex',
  computed: {
    ROUTES() {
      return ROUTES
    },
    dunningStatuses() {
      const dunningKeys = [INVOICE_STATUS.SENT, INVOICE_STATUS.REMINDED1, INVOICE_STATUS.REMINDED2]
      return this.setupStore.invoiceStatuses.filter((status) => dunningKeys.includes(status.key))
    },
    rowSums() {
      const sums = {
        netto: 0,
      }
      for (let i = 0; i < this.dunningStore.invoices.length; i++) {
        const netto = parseFloat(this.dunningStore.invoices[i].net_total)
        if (!isNaN(netto)) {
          sums.netto += netto
        }
      }
      return sums
    }
  },
  components: {
  },
  setup() {
    return {
      dunningStore: useDunningStore(),
      setupStore: useSetupStore(),
    }
  },
  data() {
    return {
      filters: {
        number: {value: null, matchMode: FilterMatchMode.STARTS_WITH},
        payment_method_id: {value: null, matchMode: FilterMatchMode.EQUALS},
        invoice_status_id: {value: null, matchMode: FilterMatchMode.EQUALS},
      },
    }
  },
  created() {
    this.getInvoices()
    this.setupStore.getInvoiceStatuses();
    this.setupStore.getPaymentMethods();
    this.setTitle(this.$t('dunning.headline'))
  },
  methods: {
    openInvoice(invoice) {
      this.dunningStore.pdf(invoice)
    },
    sendInvoice(invoice, deliveryMethod = null) {
      this.dunningStore.send(invoice, deliveryMethod)
    },
    getInvoices() {
      this.dunningStore.getInvoices()
    },
    onPage(event) {
      this.dunningStore.serverOptions = {
        ...event,
        filters: this.filters,
        sortField: this.dunningStore.serverOptions.sortField,
        sortOrder: this.dunningStore.serverOptions.sortOrder,
      }
      this.getInvoices()
    },
    onSort(event) {
      this.dunningStore.serverOptions = {
        ...this.dunningStore.serverOptions,
        ...event,
        page: 0,
        filters: this.filters,
      }
      this.getInvoices()
    },
    onFilter(data) {
      this.$nextTick(() => {
        this.dunningStore.serverOptions.filters = data.filters
        this.dunningStore.serverOptions.page = 0
        this.getInvoices()
      })
    },
  },
  watch: {
  }
}
</script>
